/**
 * Client pieces used by other screens when onboarding is on: turning a website lead into a client, picking
 * the client a quote or job is for, and recording a client's written confirmation of a job.
 */
import { useState, type FormEvent } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { BRAND } from "../../lib/config";
import { fmtDate } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { useAsync } from "../../lib/useAsync";
import { clientFromLead, clientState, jobConfirmationFile, latestTermsId, listClients, recordJobConfirmation, type ClientSummary } from "./clients-data";
import { stateChip } from "./Clients";

export const onboardingOn = () => Boolean(BRAND.onboarding);

/** Leads: make a client from the enquiry and open it. */
export function ConvertLead({ lead }: { lead: { name: string; company: string | null; email: string | null; phone: string | null } }) {
  const [busy, setBusy] = useState(false);
  if (!onboardingOn()) return null;
  return (
    <button
      type="button"
      className="btn btn--outline btn--xs"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        clientFromLead(lead).then((id) => (window.location.hash = `#/clients/${id}`), () => setBusy(false));
      }}
    >
      Make client
    </button>
  );
}

/** Quotes and jobs: which client it is for. Shows the client's onboarding status beside the choice. */
export function ClientPicker({ value, onPick }: { value: string | null; onPick: (c: ClientSummary | null) => void }) {
  const { data } = useAsync(async () => ({ clients: await listClients(), latest: await latestTermsId() }));
  if (!onboardingOn()) return null;
  const picked = data?.clients.find((c) => c.id === value);
  return (
    <label className="field">
      <span className="field__label">Client account</span>
      <select className="input" value={value ?? ""} onChange={(e) => onPick(data?.clients.find((c) => c.id === e.target.value) ?? null)}>
        <option value="">Not linked to a client</option>
        {(data?.clients ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <span className="field__hint">
        {picked ? <>{stateChip(clientState(picked, data!.latest))} <a href={`#/clients/${picked.id}`}>Open the client</a></> : "Link the client so quotes can be accepted under their signed terms."}
      </span>
    </label>
  );
}

/** Jobs: the client's written confirmation (a note, optional file), recorded once. */
export function JobConfirmation({ job, onDone }: {
  job: { id: string; customer_id?: string | null; client_confirmation_note?: string | null; client_confirmation_at?: string | null; client_confirmation_file_name?: string | null };
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  if (!onboardingOn()) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (note.trim().length < 5) return setNotice({ tone: "error", text: "Describe the confirmation: who, when and how (for example, email from Sam on 30 Oct)." });
    setBusy(true);
    try {
      const r = await recordJobConfirmation(job.id, note.trim(), file);
      ok(r.termsCurrent ? `Recorded. ${r.shiftsConfirmed ? `${r.shiftsConfirmed} waiting shift${r.shiftsConfirmed === 1 ? "" : "s"} confirmed.` : "Shifts can now be confirmed."}` : "Recorded. Shifts can be confirmed once the client has signed your current terms.");
      onDone();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">Client's written confirmation</h2></div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        {job.client_confirmation_note ? (
          <>
            <p className="prewrap">{job.client_confirmation_note}</p>
            <p className="small muted">Recorded {job.client_confirmation_at ? fmtDate(job.client_confirmation_at) : ""}. It can't be changed.</p>
            {job.client_confirmation_file_name && (
              <div><button type="button" className="btn btn--outline btn--sm" onClick={() => void openBlobInNewTab(() => jobConfirmationFile(job.id)).catch(fail)}><Icon name="file" size={16} /> {job.client_confirmation_file_name}</button></div>
            )}
          </>
        ) : (
          <form className="stack" onSubmit={submit}>
            <p className="small muted">
              {job.customer_id
                ? "For a client who has signed your current terms, their written confirmation of this job (an email, a text) is enough to confirm its shifts. Record it here once."
                : "Link this job to a client first (Edit details). A written confirmation counts once that client has signed your current terms."}
            </p>
            <label className="field"><span className="field__label">What the client confirmed, who and when</span>
              <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Email from Sam Site, 30 Oct: confirmed 3 nights from 4 Nov at the quoted rates" />
            </label>
            <label className="field"><span className="field__label">Copy of the confirmation (optional)</span>
              <input className="input" type="file" accept="application/pdf,image/jpeg,image/png,.eml" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
            <div><button type="submit" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy}>{busy ? "Saving" : "Record the confirmation"}</button></div>
          </form>
        )}
      </div>
    </div>
  );
}
